import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import GooglePlacesAutocomplete from "../../../components/GooglePlacesAutocomplete";
import api from "../../../services/api";
import { DateTime } from "luxon";

const StepFulfillmentInfo = ({ formData, setFormData, errors = {} }) => {
  const [storeLocations, setStoreLocations] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await api.get("/admin/stores");
        setStoreLocations(res.data || []);
      } catch (err) {
        console.error("Failed to load store locations", err);
      } finally {
        setLoadingStores(false);
      }
    };
    fetchStores();
  }, []);

  const renderDateValue = formData.fulfillment.date
    ? DateTime.fromISO(formData.fulfillment.date, {
        zone: "America/St_Johns",
      }).toFormat("yyyy-MM-dd")
    : "";

  const handleChange = (field) => (e) => {
    const val = e.target.value;

    if (field === "date") {
      const nstMidnight = DateTime.fromFormat(val, "yyyy-MM-dd", {
        zone: "America/St_Johns",
      }).toUTC(); // Convert to UTC

      setFormData((prev) => ({
        ...prev,
        fulfillment: {
          ...prev.fulfillment,
          date: nstMidnight.toISO(), // Store as UTC ISO string
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        fulfillment: {
          ...prev.fulfillment,
          [field]: val,
        },
      }));
    }
  };

  const handleAddressSelect = (formatted) => {
    setFormData((prev) => ({
      ...prev,
      fulfillment: {
        ...prev.fulfillment,
        location: formatted,
      },
    }));
  };

  const handlePickupLocationChange = (e) => {
    const storeAddress = e.target.value;
    setFormData((prev) => ({
      ...prev,
      fulfillment: {
        ...prev.fulfillment,
        location: storeAddress,
      },
    }));
  };

  const isPickup = formData.fulfillment.method === "pickup";

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Fulfillment Information
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">Method</FormLabel>
        <RadioGroup
          row
          value={formData.fulfillment.method}
          onChange={(e) => {
            const method = e.target.value;
            setFormData((prev) => ({
              ...prev,
              fulfillment: {
                ...prev.fulfillment,
                method,
                location: "", // Clear previous location when switching method
              },
            }));
          }}>
          <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
          <FormControlLabel
            value="delivery"
            control={<Radio />}
            label="Delivery"
          />
        </RadioGroup>
      </FormControl>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date"
            type="date"
            fullWidth
            value={renderDateValue}
            onChange={handleChange("date")}
            InputLabelProps={{ shrink: true }}
            error={!!errors.date}
            helperText={errors.date}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Time"
            fullWidth
            value={formData.fulfillment.time || ""}
            onChange={handleChange("time")}
            error={!!errors.time}
            helperText={errors.time}>
            {[
              "9:00 AM",
              "10:00 AM",
              "11:00 AM",
              "1:00 PM",
              "3:00 PM",
              "5:00 PM",
            ].map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          {isPickup ? (
            loadingStores ? (
              <CircularProgress />
            ) : (
              <TextField
                select
                label="Select Location"
                fullWidth
                value={formData.fulfillment.location || ""}
                onChange={handlePickupLocationChange}
                error={!!errors.location}
                helperText={errors.location}>
                {storeLocations.map((store) => (
                  <MenuItem key={store._id} value={store.address}>
                    {store.address}
                  </MenuItem>
                ))}
              </TextField>
            )
          ) : (
            <>
              <GooglePlacesAutocomplete
                label="Delivery Address"
                value={formData.fulfillment.location}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    fulfillment: { ...prev.fulfillment, location: val },
                  }))
                }
                onSelect={handleAddressSelect}
                confirmed={true}
              />
              {!!errors.location && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {errors.location}
                </FormHelperText>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default StepFulfillmentInfo;
